/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPalette, 
  FaImages, 
  FaGlobe, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaTimes,
  FaCloudUploadAlt,
  FaTrash,
  FaPlus
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../apis/axiosConfig';
import './EditTraining.css';

const EditTraining = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 to 2
  const [loading, setLoading] = useState(true);

  // Tab configurations:
  // - Basic Info
  // - Training Images
  // - SEO Manager
  const [sections, setSections] = useState([
    { id: 0, section: 'Basic Info', icon: FaPalette, status: 1, subtitle: 'Default', validation: 'validation-success' },
    { id: 1, section: 'Training Images', icon: FaImages, status: 1, subtitle: 'Default', validation: 'validation-success' },
    { id: 2, section: 'SEO Manager', icon: FaGlobe, status: 1, subtitle: 'Default', validation: 'validation-success' }
  ]);

  // Tab 0: Basic Info
  const [basicInfo, setBasicInfo] = useState({
    title: 'Trainings Programmes',
    description: '',
    status: 1
  });

  // Tab 1: Training Images Slides list
  const [trainingSlides, setTrainingSlides] = useState([
    { id: '1', type: 'image', heading: '', description: '', image: '', video: '', alt: '', status: 1, sort: '1', thumb: 0 }
  ]);

  // Tab 2: SEO Manager
  const [seo, setSeo] = useState({
    meta_title: 'Trainings Appointments - Aristo India, Bangalore',
    meta_description: '',
    meta_keywords: []
  });
  const [keywordInput, setKeywordInput] = useState('');

  const fileInputRefs = useRef({});

  // Quill config
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'bold', 'italic', 'underline',
    'header', 'size',
    'list', 'bullet', 'align'
  ];

  useEffect(() => {
    fetchTrainingData();
  }, []);

  const fetchTrainingData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pages/training');
      if (res && res.data && res.data.success) {
        const { page, sections: dbSections, contents } = res.data;

        // 1. Set Page properties
        if (page) {
          setBasicInfo(prev => ({
            ...prev,
            title: page.page_heading || 'Trainings Programmes',
            status: page.status === 1 ? 1 : 0
          }));
        }

        // 2. Parse section status toggles
        if (dbSections && dbSections.length > 0) {
          const updatedSec = [...sections];
          dbSections.forEach(s => {
            const index = updatedSec.findIndex(us => us.id === s.id);
            if (index !== -1) {
              updatedSec[index].status = s.status === 1 ? 1 : 0;
              updatedSec[index].validation = s.status === 1 ? 'validation-success' : 'validation-error';
            }
          });
          setSections(updatedSec);
        }

        // 3. Parse section contents
        if (contents && contents.length > 0) {
          contents.forEach(item => {
            if (item.section_id === 0) {
              // Basic Info
              try {
                const info = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
                setBasicInfo(prev => ({
                  ...prev,
                  title: info.heading || prev.title,
                  description: info.description || ''
                }));
              } catch (e) {
                console.error("Error parsing basic info content:", e);
              }
            } else if (item.section_id === 1) {
              // Training Images
              try {
                const slides = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
                if (Array.isArray(slides) && slides.length > 0) {
                  setTrainingSlides(slides);
                }
              } catch (e) {
                console.error("Error parsing training slides content:", e);
              }
            } else if (item.section_id === 2) {
              // SEO Manager
              try {
                const seoData = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
                const keywordsArray = seoData.meta_keywords 
                  ? seoData.meta_keywords.split(',').map(k => k.trim()).filter(Boolean)
                  : [];
                setSeo({
                  meta_title: seoData.meta_title || '',
                  meta_description: seoData.meta_description || '',
                  meta_keywords: keywordsArray
                });
              } catch (e) {
                console.error("Error parsing SEO content:", e);
              }
            }
          });
        }
      }
    } catch (err) {
      console.error("Error loading Trainings configuration:", err);
      toast.error("Failed to load Trainings page data");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('blob:') || image.startsWith('http')) return image;
    const backendHost = (process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000').replace(/\/$/, '');
    return `${backendHost}/uploads/${image}`;
  };

  // Toggle switch sidebar section status
  const handleToggleSectionStatus = (idx, e) => {
    e.stopPropagation();
    const updated = [...sections];
    const newStatus = updated[idx].status === 1 ? 0 : 1;
    updated[idx].status = newStatus;
    updated[idx].validation = newStatus === 1 ? 'validation-success' : 'validation-error';
    
    if (idx === 0) {
      setBasicInfo(prev => ({ ...prev, status: newStatus }));
    }
    
    setSections(updated);
  };

  // Tag inputs helper
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = keywordInput.trim().replace(/,$/, '');
      if (val && !seo.meta_keywords.includes(val)) {
        setSeo({
          ...seo,
          meta_keywords: [...seo.meta_keywords, val]
        });
      }
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setSeo({
      ...seo,
      meta_keywords: seo.meta_keywords.filter((_, idx) => idx !== indexToRemove)
    });
  };

  // Training Slides CRUD
  const handleAddSlide = () => {
    const newSlide = {
      id: String(trainingSlides.length + 1),
      type: 'image',
      heading: '',
      description: '',
      image: '',
      video: '',
      alt: '',
      status: 1,
      sort: '1',
      thumb: 0
    };
    setTrainingSlides([...trainingSlides, newSlide]);
  };

  const handleRemoveSlide = (idx) => {
    if (trainingSlides.length === 1) {
      toast.warning('At least one slide configuration is required.');
      return;
    }
    setTrainingSlides(trainingSlides.filter((_, i) => i !== idx));
  };

  const handleSlideImageChange = async (e, slideIdx) => {
    const file = e.target.files[0];
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    const updated = [...trainingSlides];
    updated[slideIdx].image = tempUrl;
    updated[slideIdx]._file = file;
    setTrainingSlides(updated);

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axiosInstance.post('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.filename) {
        const finalUrl = res.data.filename;
        const fresh = [...trainingSlides];
        fresh[slideIdx].image = finalUrl;
        delete fresh[slideIdx]._file;
        setTrainingSlides(fresh);
        toast.success(`Slide ${slideIdx + 1} image uploaded!`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Image upload failed');
    }
  };

  // Save Settings
  const handleSaveSettings = async () => {
    if (!basicInfo.title.trim()) {
      toast.warning('Please enter Page Title!');
      return;
    }

    // Clean training slides
    const cleanedSlides = trainingSlides.map(s => {
      const copy = { ...s };
      delete copy._file;
      return copy;
    });

    try {
      const payload = {
        page_heading: basicInfo.title,
        status: basicInfo.status,
        meta_title: seo.meta_title,
        meta_description: seo.meta_description,
        meta_keywords: seo.meta_keywords.join(','),
        sections: sections.map(s => ({ id: s.id, status: s.status })),
        contents: [
          {
            section_id: 0,
            content: {
              heading: basicInfo.title,
              description: basicInfo.description
            },
            status: sections[0].status
          },
          {
            section_id: 1,
            content: cleanedSlides,
            status: sections[1].status
          },
          {
            section_id: 2,
            content: {
              meta_title: seo.meta_title,
              meta_description: seo.meta_description,
              meta_keywords: seo.meta_keywords.join(',')
            },
            status: sections[2].status
          }
        ]
      };

      const res = await axiosInstance.patch('/pages/training', payload);
      if (res.data && res.data.success) {
        toast.success('Training Programs saved successfully!');
        
        // Update checkmarks status
        const updatedSec = [...sections];
        updatedSec[0].validation = basicInfo.status === 1 ? 'validation-success' : 'validation-error';
        updatedSec[1].validation = sections[1].status === 1 ? 'validation-success' : 'validation-error';
        updatedSec[2].validation = basicInfo.status === 1 ? 'validation-success' : 'validation-error';
        setSections(updatedSec);
      } else {
        toast.error(res.data.message || 'Error updating Training Programs');
      }
    } catch (e) {
      console.error("Save error:", e);
      toast.error('Server error while saving Training Programs configuration');
    }
  };

  if (loading) {
    return (
      <div className="edit-training-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <h3 style={{ color: '#c4a882' }}>Loading Training Configuration...</h3>
      </div>
    );
  }

  return (
    <div className="edit-training-container animate-fade-up">
      <div className="edit-training-header">
        <h1 className="edit-training-title">Trainings Programmes</h1>
      </div>

      <div className="edit-training-grid">
        {/* Sticky Left Sidebar */}
        <div className="edit-training-sidebar">
          <ul className="edit-training-nav-list">
            {sections.map((sec, idx) => {
              const IconComp = sec.icon;
              return (
                <li 
                  key={sec.id}
                  className={`edit-training-nav-item ${activeTab === idx ? 'active' : ''}`}
                  onClick={() => setActiveTab(idx)}
                >
                  <div className="edit-training-nav-left">
                    <span className="edit-training-nav-icon"><IconComp /></span>
                    <div className="edit-training-nav-text">
                      <span className="nav-item-title">{sec.section}</span>
                      <span className="nav-item-subtitle">{sec.subtitle}</span>
                    </div>
                  </div>

                  <div className="edit-training-nav-right">
                    <span className={`status-indicator ${sec.validation}`}>
                      {sec.validation === 'validation-success' && <FaCheckCircle />}
                      {sec.validation === 'validation-error' && <FaTimesCircle />}
                    </span>

                    <label className="switch-toggle" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={sec.status === 1}
                        onChange={(e) => handleToggleSectionStatus(idx, e)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Scrollable Right Panel */}
        <div className="edit-training-content-panel">
          
          {/* TAB 0: Basic Info */}
          {activeTab === 0 && (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-training-panel-title">Basic Info</h2>
              
              <div className="training-card-block">
                <div className="form-group-custom">
                  <label className="label-custom">Title <span className="req">*</span></label>
                  <input 
                    type="text" 
                    className="input-custom" 
                    placeholder="Enter Title..."
                    value={basicInfo.title}
                    onChange={(e) => setBasicInfo({ ...basicInfo, title: e.target.value })}
                  />
                </div>

                <div className="form-group-custom">
                  <label className="label-custom">Short Description <span className="req">*</span></label>
                  <div className="quill-editor-wrapper">
                    <ReactQuill 
                      theme="snow"
                      value={basicInfo.description}
                      onChange={(val) => setBasicInfo({ ...basicInfo, description: val })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter Short Description..."
                    />
                  </div>
                  <span className="helper-text text-right" style={{ display: 'block', marginTop: '0.4rem' }}>
                    Note: Min length: 100 Characters | Max length: 2500 Characters
                  </span>
                </div>

                <div className="form-group-custom" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <label className="label-custom" style={{ marginBottom: 0 }}>Page Active Status</label>
                  <label className="switch-toggle">
                    <input 
                      type="checkbox" 
                      checked={basicInfo.status === 1}
                      onChange={(e) => {
                        const newStatus = e.target.checked ? 1 : 0;
                        setBasicInfo({ ...basicInfo, status: newStatus });
                        
                        const updated = [...sections];
                        updated[0].status = newStatus;
                        updated[0].validation = newStatus === 1 ? 'validation-success' : 'validation-error';
                        updated[2].status = newStatus;
                        updated[2].validation = newStatus === 1 ? 'validation-success' : 'validation-error';
                        setSections(updated);
                      }}
                    />
                    <span className="switch-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: Training Images */}
          {activeTab === 1 && (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-training-panel-title">Training Images</h2>
              
              {sections[1].status === 0 ? (
                <div className="oops-disabled-overlay animate-fade-up">
                  <div className="oops-card">
                    <div className="oops-icon">✖</div>
                    <h3 className="oops-title">Oops!</h3>
                    <p className="oops-message">Please enable or activate Training Images to modify the content.</p>
                    <button 
                      type="button" 
                      className="btn-enable-oops"
                      onClick={() => {
                        const updated = [...sections];
                        updated[1].status = 1;
                        updated[1].validation = 'validation-success';
                        setSections(updated);
                      }}
                    >
                      Enable / Activate
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {trainingSlides.map((slide, sIdx) => (
                    <div key={sIdx} className="training-card-block" style={{ marginBottom: '1.5rem', position: 'relative' }}>
                      
                      {/* Slide Header Row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem', marginBottom: '1.25rem' }}>
                        <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#c4a882' }}>Slide {sIdx + 1}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <label className="switch-toggle" style={{ transform: 'scale(0.85)', marginBottom: 0 }}>
                            <input 
                              type="checkbox" 
                              checked={slide.status === 1}
                              onChange={(e) => {
                                const u = [...trainingSlides];
                                u[sIdx].status = e.target.checked ? 1 : 0;
                                setTrainingSlides(u);
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <button 
                            type="button" 
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', padding: 0 }}
                            onClick={() => handleRemoveSlide(sIdx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      {/* Flex/Grid split like user screenshot */}
                      <div className="slide-content-split">
                        
                        {/* Left half: Controls */}
                        <div className="slide-split-left">
                          <div className="form-group-custom">
                            <label className="label-custom">Banner Type <span className="req">*</span></label>
                            <select 
                              className="input-custom" 
                              style={{ cursor: 'pointer' }}
                              value={slide.type || 'image'}
                              onChange={(e) => {
                                const u = [...trainingSlides];
                                u[sIdx].type = e.target.value;
                                setTrainingSlides(u);
                              }}
                            >
                              <option value="image">Image</option>
                              <option value="video">Video</option>
                            </select>
                          </div>

                          <div className="form-group-custom">
                            <label className="label-custom">URL <span className="req">*</span></label>
                            <div className="choose-file-row">
                              <button 
                                type="button" 
                                className="btn-choose-file"
                                onClick={() => fileInputRefs.current[`training_slide_${sIdx}`]?.click()}
                              >
                                Choose file
                              </button>
                              <span className="file-name-label">
                                {slide.image ? (typeof slide.image === 'string' && slide.image.startsWith('blob:') ? 'New file selected' : slide.image.split('-').pop()) : 'No file chosen'}
                              </span>
                            </div>
                            <input 
                              type="file" 
                              accept="image/*"
                              style={{ display: 'none' }}
                              ref={el => fileInputRefs.current[`training_slide_${sIdx}`] = el}
                              onChange={(e) => handleSlideImageChange(e, sIdx)}
                            />
                          </div>
                        </div>

                        {/* Right half: Preview & Alt Text */}
                        <div className="slide-split-right">
                          <div className="form-group-custom">
                            <label className="label-custom">Preview</label>
                            <div 
                              className="training-preview-box"
                              style={{ background: slide.image ? '#ffffff' : 'transparent' }}
                            >
                              {slide.image ? (
                                <img src={getImageUrl(slide.image)} alt={slide.alt || 'Training Preview'} />
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#94a3b8' }}>
                                  <FaCloudUploadAlt size={32} style={{ color: '#c4a882', marginBottom: '0.4rem' }} />
                                  <span style={{ fontSize: '0.8rem' }}>Upload slide image</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="form-group-custom">
                            <label className="label-custom">Alt Text <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Alt Text"
                              value={slide.alt || ''}
                              onChange={(e) => {
                                const u = [...trainingSlides];
                                u[sIdx].alt = e.target.value;
                                setTrainingSlides(u);
                              }}
                            />
                          </div>
                        </div>
                      </div>

                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <button 
                      type="button" 
                      className="btn-save" 
                      style={{ background: 'transparent', border: '1px dashed #c4a882', color: '#c4a882', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: 'none' }}
                      onClick={handleAddSlide}
                    >
                      <FaPlus /> Add Training Image
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 2: SEO Manager */}
          {activeTab === 2 && (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-training-panel-title">SEO Manager</h2>
              
              <div className="training-card-block">
                <div className="form-group-custom">
                  <label className="label-custom">Meta Title <span className="req">*</span></label>
                  <input 
                    type="text" 
                    className="input-custom" 
                    placeholder="Enter Meta Title..."
                    value={seo.meta_title}
                    onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })}
                  />
                </div>

                <div className="form-group-custom">
                  <label className="label-custom">Meta Description <span className="req">*</span></label>
                  <textarea 
                    className="textarea-custom" 
                    rows={4}
                    placeholder="Enter Meta Description..."
                    value={seo.meta_description}
                    onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })}
                  />
                </div>

                <div className="form-group-custom">
                  <label className="label-custom">Meta Keywords</label>
                  
                  {/* Tag Pill Selector */}
                  <div className="keywords-pill-container">
                    {seo.meta_keywords.map((tag, idx) => (
                      <span key={idx} className="keyword-pill">
                        {tag}
                        <button 
                          type="button" 
                          className="keyword-pill-remove" 
                          onClick={() => handleRemoveKeyword(idx)}
                        >
                          <FaTimes size={10} />
                        </button>
                      </span>
                    ))}
                    <input 
                      type="text" 
                      className="keywords-pills-input" 
                      placeholder={seo.meta_keywords.length === 0 ? "Type keyword and press Enter..." : "Add keyword..."}
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeywordKeyDown}
                    />
                  </div>
                  <span className="helper-text">Type a keyword and press Enter or Comma (,) to add. Click &quot;x&quot; to remove.</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="edit-training-footer">
        <button 
          type="button" 
          className="btn-cancel"
          onClick={() => navigate('/page-list')}
        >
          Cancel
        </button>
        <button 
          type="button" 
          className="btn-save"
          onClick={handleSaveSettings}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default EditTraining;
